/**
 * "Didn't arrive — send it again" (lifecycle audit, 10/29).
 *
 * On a customer's Contacts tab: every email to them that bounced and hasn't
 * arrived since. Once the address is fixed (edit it here, or add the right
 * one), each item gets one button — "Send it again to <new address>" — that
 * re-sends just what bounced through its normal Send. Until then the row says
 * to fix the address first. Hidden when nothing bounced.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MailWarning, Send } from "lucide-react";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { bouncedItemLabel, sendAgainCalls, type BouncedSendItem } from "./send-again";

export function BouncedSendsCard({ customerId }: { customerId: string }) {
    const qc = useQueryClient();
    const q = useQuery({
        queryKey: ["customer", customerId, "contacts", "bounced"],
        queryFn: () => api.get<{ items: BouncedSendItem[] }>(`/api/customers/${customerId}/bounced-sends`),
    });
    const send = useMutation({
        mutationFn: async (item: BouncedSendItem) => {
            for (const c of sendAgainCalls(item)) {
                if (c.method === "patch") await api.patch(c.url, c.body);
                else await api.post(c.url, c.body);
            }
        },
        onSuccess: (_r, item) => {
            toast.success(`${bouncedItemLabel(item)} sent again to ${item.sendTo}.`);
            qc.invalidateQueries({ queryKey: ["customer", customerId] });
        },
        onError: (e) => {
            const msg = e instanceof ApiError ? e.message : null;
            toast.error(msg ?? "It didn't send. Nothing changed — try again, or open the item and send it from there.");
        },
    });

    const items = q.data?.items ?? [];
    if (items.length === 0) return null;
    return (
        <div
            className="rounded-xl border border-destructive/40 bg-destructive/5 p-3"
            data-testid="bounced-sends-card"
            role="region"
            aria-label="Emails that didn't arrive"
        >
            <p className="flex items-center gap-2 text-sm font-bold text-destructive">
                <MailWarning className="h-4 w-4" />
                {items.length === 1 ? "1 email didn't arrive" : `${items.length} emails didn't arrive`}
            </p>
            <ul className="mt-2 space-y-2">
                {items.map((item) => {
                    const calls = sendAgainCalls(item);
                    const when = new Date(item.at).toLocaleDateString("en-US", { month: "short", day: "numeric" });
                    return (
                        <li
                            key={item.sendId}
                            className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border bg-background px-3 py-2 text-[13px]"
                        >
                            <div className="min-w-0 flex-1">
                                <span className="font-semibold">{bouncedItemLabel(item)}</span>
                                <span className="text-muted-foreground">
                                    {" "}
                                    · to {item.recipient} · {when}
                                </span>
                                {item.reason && (
                                    <span
                                        className="block truncate text-[11.5px] text-muted-foreground"
                                        title={item.reason}
                                    >
                                        {item.reason}
                                    </span>
                                )}
                            </div>
                            {calls.length > 0 ? (
                                <Button
                                    size="sm"
                                    onClick={() => send.mutate(item)}
                                    disabled={send.isPending}
                                    data-testid="send-it-again"
                                >
                                    <Send className="mr-1.5 h-3.5 w-3.5" />
                                    Send it again to {item.sendTo}
                                </Button>
                            ) : (
                                <span className="text-[12px] text-muted-foreground">
                                    {item.sendTo
                                        ? "Open it and send it from there."
                                        : "Fix the address below first — then Send it again shows here."}
                                </span>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
